/** M8-26 品牌使用授权：纯渲染/格式化（无 DOM、无网络 —— 便于 `node --test` 直接测）。
 *
 * 状态码单源是 `sql/dc_mysql8.sql` 的 `bff_brand_owner_authorizations`：
 * `pending → active / rejected / withdrawn`，停用 `disabled`（批准即 active）。
 */
import { escHtml } from "../lib/hints.js";

export const AUTH_STATUS_LABEL = {
  pending: "待审核",
  active: "已通过",
  rejected: "已驳回",
  withdrawn: "已撤回",
  disabled: "已停用",
};

/** 认领单的状态词与授权单不同：批准后的语义是「你成了所有者」，不是「你获准使用」。 */
export const CLAIM_STATUS_LABEL = {
  pending: "待平台核验",
  approved: "已认领",
  rejected: "已驳回",
  withdrawn: "已撤回",
};

const APPLY_STATUS_HINT = {
  pending: "已有待审申请",
  active: "已获授权",
};

export function statusLabel(status) {
  return AUTH_STATUS_LABEL[status] || status || "";
}

export function statusTagClass(status) {
  if (status === "active") return "brand-tag ok";
  if (status === "rejected" || status === "disabled") return "brand-tag warn";
  return "brand-tag";
}

/** 检索结果里的「为什么不能申请」：后端已给 `block_reason`，没给就按申请状态说。 */
export function applyBlockText(item) {
  if (!item) return "";
  if (item.can_apply) return "";
  return item.block_reason || APPLY_STATUS_HINT[item.my_application_status] || "暂不能申请";
}

/** 授权范围一句话（批准时填的渠道 / 区域 / 独占）。 */
export function scopeSummary(scope) {
  const s = scope || {};
  const parts = [];
  const channels = Array.isArray(s.channels) ? s.channels : [];
  const regions = Array.isArray(s.regions) ? s.regions : [];
  if (channels.length) parts.push(`渠道 ${channels.join("、")}`);
  if (regions.length) parts.push(`区域 ${regions.join("、")}`);
  if (s.exclusive) parts.push("独占");
  return parts.join(" · ");
}

/** 逗号/顿号/空白分隔的自由文本 → 字符串数组（批准表单的输入解析）。 */
export function splitList(text) {
  return String(text || "")
    .split(/[,，、;；\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 20);
}

/** 平台现有品牌检索结果。 */
export function renderSearchRows(items) {
  const rows = (items || [])
    .map((it) => {
      const block = applyBlockText(it);
      // 没有所有者的牌子，申请一定被挡（没人审批）—— 这时画「认领品牌」而不是一句死话，
      // 认领过就不再给按钮，避免同一品牌重复提交（后端也会 409）。
      const claimPending = it.my_claim_status === "pending";
      let action;
      if (it.can_apply) {
        action = `<button type="button" class="secondary sm btn-brand-apply" data-brand-id="${escHtml(
          it.brand_id
        )}" data-brand-name="${escHtml(it.brand_name || "")}">申请授权</button>`;
      } else if (it.owner_claimed === false && !claimPending) {
        action = `<button type="button" class="secondary sm btn-brand-claim" data-brand-id="${escHtml(
          it.brand_id
        )}" data-brand-name="${escHtml(it.brand_name || "")}">认领品牌</button>`;
      } else {
        action = `<span class="muted">${escHtml(
          claimPending ? "认领已提交，等平台核验" : block
        )}</span>`;
      }
      return `<tr class="brand-search-row${it.can_apply ? "" : " is-blocked"}">
        <td><code>${escHtml(it.brand_code || "")}</code></td>
        <td><strong>${escHtml(it.brand_name || "")}</strong></td>
        <td>${escHtml(it.owner_public_name || "—")}</td>
        <td>${
          it.my_application_status
            ? `<span class="${statusTagClass(it.my_application_status)}">${escHtml(
                statusLabel(it.my_application_status)
              )}</span>`
            : '<span class="muted">—</span>'
        }</td>
        <td>${action}</td>
      </tr>`;
    })
    .join("");
  return `<div class="brand-table-wrap"><table class="brand-table">
    <thead><tr><th>品牌编码</th><th>品牌名称</th><th>所有者</th><th>我的申请</th><th></th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

/** 我发起的申请（applications）。 */
export function renderMyApplications(items) {
  const list = items || [];
  if (!list.length) {
    return "<p class='mc-desc muted'>还没有提交过品牌授权申请。</p>";
  }
  const rows = list
    .map((it) => {
      const reason =
        it.status === "rejected" && it.reject_reason
          ? `<div class="muted">驳回原因：${escHtml(it.reject_reason)}</div>`
          : "";
      const scope = scopeSummary(it.scope);
      const actions = [];
      if (it.can_withdraw) {
        actions.push(
          `<button type="button" class="secondary sm btn-brand-withdraw" data-auth-id="${escHtml(
            it.authorization_id
          )}">撤回</button>`
        );
      }
      return `<tr>
        <td><strong>${escHtml(it.brand_name || it.brand_id || "")}</strong>
          <div class="muted">${escHtml(it.brand_code || "")}</div></td>
        <td>${escHtml(it.owner_merchant_name || it.owner_merchant_id || "—")}</td>
        <td><span class="${statusTagClass(it.status)}">${escHtml(statusLabel(it.status))}</span></td>
        <td class="muted">${escHtml((it.created_at || "").slice(0, 16) || "—")}</td>
        <td>${escHtml(scope || "—")}${reason}</td>
        <td>${actions.join(" ")}</td>
      </tr>`;
    })
    .join("");
  return `<div class="brand-table-wrap"><table class="brand-table">
    <thead><tr><th>品牌</th><th>所有者</th><th>状态</th><th>提交</th><th>授权范围 / 说明</th><th></th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

/** 我作为所有者收到的申请（inbox）。 */
export function renderInbox(items) {
  const list = items || [];
  if (!list.length) {
    return "<p class='mc-desc muted'>还没有商家申请使用你的品牌。</p>";
  }
  const rows = list
    .map((it) => {
      const actions = [];
      if (it.can_review) {
        actions.push(
          `<button type="button" class="sm btn-brand-approve" data-auth-id="${escHtml(
            it.authorization_id
          )}">批准</button>`,
          `<button type="button" class="secondary sm btn-brand-reject" data-auth-id="${escHtml(
            it.authorization_id
          )}">驳回</button>`
        );
      }
      if (it.can_disable) {
        actions.push(
          `<button type="button" class="secondary sm btn-brand-disable" data-auth-id="${escHtml(
            it.authorization_id
          )}">停用</button>`
        );
      }
      const extra =
        it.status === "rejected" && it.reject_reason
          ? `<div class="muted">驳回原因：${escHtml(it.reject_reason)}</div>`
          : "";
      const scope = scopeSummary(it.scope);
      return `<tr>
        <td><strong>${escHtml(it.grantee_merchant_name || it.grantee_merchant_id || "")}</strong></td>
        <td>${escHtml(it.brand_name || it.brand_id || "")}
          <div class="muted">${escHtml(it.brand_code || "")}</div></td>
        <td><span class="${statusTagClass(it.status)}">${escHtml(statusLabel(it.status))}</span></td>
        <td class="muted">${escHtml((it.created_at || "").slice(0, 16) || "—")}</td>
        <td class="muted">${escHtml(it.apply_note || "—")}${extra}${
        scope ? `<div>${escHtml(scope)}</div>` : ""
      }</td>
        <td>${actions.join(" ")}</td>
      </tr>`;
    })
    .join("");
  return `<div class="brand-table-wrap"><table class="brand-table">
    <thead><tr><th>申请商家</th><th>品牌</th><th>状态</th><th>提交</th><th>经营说明</th><th></th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

/** 我提交的认领（`bff_brand_owner_claims`）：等核验 / 已认领 / 已驳回。 */
export function renderMyClaims(items) {
  const list = items || [];
  if (!list.length) {
    return "<p class='mc-desc muted'>还没有提交过品牌认领。牌子还没人认领时，检索结果里会出现「认领品牌」。</p>";
  }
  const rows = list
    .map((it) => {
      const reason =
        it.status === "rejected" && it.reject_reason
          ? `<div class="muted">驳回原因：${escHtml(it.reject_reason)}</div>`
          : "";
      const actions = it.can_withdraw
        ? `<button type="button" class="secondary sm btn-brand-claim-withdraw" data-claim-id="${escHtml(
            it.claim_id
          )}">撤回</button>`
        : "";
      const lab = CLAIM_STATUS_LABEL[it.status] || it.status || "";
      return `<tr>
        <td><strong>${escHtml(it.brand_name || it.brand_id || "")}</strong>
          <div class="muted">${escHtml(it.brand_code || "")}</div></td>
        <td><span class="${statusTagClass(it.status)}">${escHtml(lab)}</span></td>
        <td class="muted">${escHtml((it.created_at || "").slice(0, 16) || "—")}</td>
        <td class="muted">${escHtml(it.claim_note || "—")}${reason}</td>
        <td>${actions}</td>
      </tr>`;
    })
    .join("");
  return `<div class="brand-table-wrap"><table class="brand-table">
    <thead><tr><th>品牌</th><th>状态</th><th>提交</th><th>认领说明</th><th></th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}
